import * as React from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { TabValidationIndicator, useTabValidation } from "./profile-form-components/TabValidationIndicator";
import { validateProfile, hasValidationErrors, validateExperience, validateEducation } from "../utils/validation";
import { ProfileCompleteness } from "./ProfileCompleteness";
import { submitProfile } from "../utils/profileSubmission";
import { ACTIONS, ProfileFormReducer } from "./profile-form-components/ProfileFormReducer";
import { Profile, InitialState, DispatchAction } from "../types/Profile";
import { ValidationErrorSummary } from "./profile-form-components/ValidationErrorSummary";
import GeneralInfo from "./profile-form-components/GeneralInfo";
import Documents from "./profile-form-components/Documents";
import Experience from "./profile-form-components/Experience";
import Availability from "./profile-form-components/Availability";

function initialState(
  profile: Profile,
  job_types: any[],
  hiring_types: any[],
  skills: any[],
  skill_categories: any[],
  authenticity_token: string,
): InitialState {
  const educations = profile.educations || [];
  const experiences = profile.experiences || [];

  return {
    options: { job_types, hiring_types },
    authenticity_token,
    skills,
    skill_categories,
    processing: false,
    error: false,
    experience_selected: false,
    experience: false,
    tabIndex: 0,
    profile: {
      first_name: "",
      last_name: "",
      phone: "",
      zip: "",
      position_type_ids: profile.position_types?.map((p: any) => p.id) || [],
      skill_ids: profile.skills?.map((s: any) => s.id) || [],
      job_type_ids: profile.job_types?.map((j: any) => j.id) || [],
      ...profile,
      experiences,
      educations,
    },
    validation_errors: {
      first_name: null,
      last_name: null,
      phone: null,
      zip: null,
      street_address: null,
      city: null,
      state: null,
      hourly_rate: null,
      maximum_traveling_distance: null,
      positions: null,
      availability: null,
      experience_or_resume: null,
    },
    nested_validation_errors: {
      experiences: experiences.map(() => ({
        employer: null,
        title: null,
        start_date: null,
        end_date: null,
        description: null,
      })),
      educations: educations.map(() => ({
        school_name: null,
        qualification: null,
        start_date: null,
        end_date: null,
        description: null,
      })),
    },
  };
}

export const ProfileFormContext = React.createContext<[InitialState, React.Dispatch<DispatchAction>]>([{} as InitialState, () => { }]);

interface ProfileFormProps {
  skills: any[];
  skill_categories: any[];
  profile: Profile;
  job_types: any[];
  hiring_types: any[];
  authenticity_token: string;
}

export default function ProfileEditForm({
  skills,
  skill_categories,
  profile,
  job_types,
  hiring_types,
  authenticity_token,
}: ProfileFormProps) {
  const [state, dispatch] = React.useReducer(
    ProfileFormReducer,
    initialState(
      profile,
      job_types,
      hiring_types,
      skills,
      skill_categories,
      authenticity_token,
    ),
  );

  // Calculate tab validation status
  const tabValidation = useTabValidation(state.validation_errors, state.nested_validation_errors, state.profile);

  // Run comprehensive validation whenever profile data changes
  React.useEffect(() => {
    const validationErrors = validateProfile(state.profile, { mode: 'edit' });

    // Only update if validation errors have actually changed
    const currentErrors = JSON.stringify(state.validation_errors);
    const newErrors = JSON.stringify(validationErrors);

    if (currentErrors !== newErrors) {
      dispatch({
        type: ACTIONS.SET_VALIDATION_ERRORS,
        payload: validationErrors
      });
    }
  }, [state.profile]); // Run validation whenever profile data changes

  // Run nested validation for experiences and educations
  React.useEffect(() => {
    let hasChanges = false;
    const updatedNestedErrors = { ...state.nested_validation_errors };

    // Validate experiences
    if (state.profile.experiences && state.profile.experiences.length > 0) {
      const experienceErrors: { [key: string]: { [key: string]: string | null } } = {};

      state.profile.experiences.forEach((experience: any, index: number) => {
        const errors = validateExperience(experience);
        if (Object.values(errors).some(error => error !== null)) {
          experienceErrors[index] = errors;
        }
      });

      // Convert to array format expected by ProfileEditForm
      const newExperienceErrors = state.profile.experiences.map((_: any, index: number) =>
        experienceErrors[index] || {
          employer: null,
          title: null,
          start_date: null,
          end_date: null,
          description: null,
        }
      );

      // Check if experience errors have changed
      const currentExperienceErrors = JSON.stringify(updatedNestedErrors.experiences);
      const newExperienceErrorsString = JSON.stringify(newExperienceErrors);

      if (currentExperienceErrors !== newExperienceErrorsString) {
        updatedNestedErrors.experiences = newExperienceErrors;
        hasChanges = true;
      }
    }

    // Validate educations
    if (state.profile.educations && state.profile.educations.length > 0) {
      const educationErrors: { [key: string]: { [key: string]: string | null } } = {};

      state.profile.educations.forEach((education: any, index: number) => {
        const errors = validateEducation(education);
        if (Object.values(errors).some(error => error !== null)) {
          educationErrors[index] = errors;
        }
      });

      // Convert to array format expected by ProfileEditForm
      const newEducationErrors = state.profile.educations.map((_: any, index: number) =>
        educationErrors[index] || {
          school_name: null,
          qualification: null,
          start_date: null,
          end_date: null,
          description: null,
        }
      );

      // Check if education errors have changed
      const currentEducationErrors = JSON.stringify(updatedNestedErrors.educations);
      const newEducationErrorsString = JSON.stringify(newEducationErrors);

      if (currentEducationErrors !== newEducationErrorsString) {
        updatedNestedErrors.educations = newEducationErrors;
        hasChanges = true;
      }
    }

    // Only dispatch if there are changes
    if (hasChanges) {
      dispatch({
        type: ACTIONS.SET_NESTED_VALIDATION_ERRORS,
        payload: updatedNestedErrors
      });
    }
  }, [state.profile.experiences, state.profile.educations]); // Run when experiences or educations change

  const nextTab = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    setTimeout(() => {
      dispatch({ type: ACTIONS.SET_TAB_INDEX, payload: state.tabIndex + 1 });
    }, 200);
  };

  const tabStyles = "react-tabs__tab text-center flex-grow-1";

  // Function to find the first tab with validation errors
  const getFirstTabWithErrors = () => {
    const fieldToTab = {
      // General Info (Tab 0)
      'first_name': 0,
      'last_name': 0,
      'phone': 0,
      'zip': 0,
      'street_address': 0,
      'city': 0,
      'state': 0,

      // Experience (Tab 2)
      'experience_or_resume': 2,
      'experiences': 2,
      'educations': 2,

      // Availability (Tab 3)
      'hourly_rate': 3,
      'maximum_traveling_distance': 3,
      'positions': 3,
      'availability': 3,
      'position_type_ids': 3,
      'job_type_ids': 3,
    };

    // Check flat validation errors
    for (const field in state.validation_errors) {
      if (state.validation_errors[field]) {
        if (fieldToTab[field] !== undefined) {
          return fieldToTab[field];
        }
        // Check for nested field patterns
        if (field.startsWith('experiences') || field.startsWith('educations')) {
          return 2;
        }
      }
    }

    // Check nested validation errors
    if (state.nested_validation_errors.experiences?.some(exp => exp && Object.values(exp).some(error => error))) {
      return 2;
    }
    if (state.nested_validation_errors.educations?.some(edu => edu && Object.values(edu).some(error => error))) {
      return 2;
    }

    return 0; // Default to first tab
  };

  const submitForm = () => {
    // Check for validation errors before submitting
    const hasErrors = hasValidationErrors(state.validation_errors);

    // Always validate individual experience/education fields if they were filled out, regardless of resume
    const hasNestedErrors = state.nested_validation_errors.experiences?.some((exp: any) => exp && Object.values(exp).some(error => error)) ||
      state.nested_validation_errors.educations?.some((edu: any) => edu && Object.values(edu).some(error => error));

    if (hasErrors || hasNestedErrors) {
      // Navigate to the first tab with errors
      const firstErrorTab = getFirstTabWithErrors();
      dispatch({ type: ACTIONS.SET_TAB_INDEX, payload: firstErrorTab });

      // Scroll to form
      setTimeout(() => {
        const element = document.getElementById("profileeditformcontainer");
        element?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);

      // Show validation errors and prevent submission
      dispatch({
        type: ACTIONS.FORM_SUBMIT_VALIDATION_ERRORS,
        payload: state.validation_errors
      });
      return;
    }

    // If no validation errors, proceed with form submission
    submitProfile(state.profile, dispatch, state.authenticity_token);
  };

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [state.tabIndex]);

  return (
    <ProfileFormContext.Provider value={[state, dispatch]}>

      {state.processing && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="h5 text-dark">Saving your profile...</div>
            <div className="text-muted">Please wait while we update your information.</div>
          </div>
        </div>
      )}

      {state.error && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className="alert alert-danger" role="alert" style={{ maxWidth: '500px' }}>
            <h4 className="alert-heading">
              <i className="fas fa-exclamation-circle me-2"></i>
              Error!
            </h4>
            <p>There was an error submitting your profile. Please check your information and try again.</p>
            <hr />
            <p className="mb-0">If the problem persists, please contact support.</p>
          </div>
        </div>
      )}

      {!state.processing && !state.error && (
        <Tabs
          selectedIndex={state.tabIndex}
          onSelect={(index: number) =>
            dispatch({ type: ACTIONS.SET_TAB_INDEX, payload: index })
          }
          className="profile-form-tabs-container"
        >
          <TabList className="d-flex justify-content-between gap-0">
            <Tab className={tabStyles}>
              <i className="fas fa-user tab-icon"></i>
              <span className="tab-text-desktop">General Info</span>
              <TabValidationIndicator {...tabValidation.generalInfo} />
            </Tab>
            <Tab className={tabStyles}>
              <i className="fas fa-file tab-icon"></i>
              <span className="tab-text-desktop">Documents</span>
              <TabValidationIndicator {...tabValidation.documents} />
            </Tab>
            <Tab className={tabStyles}>
              <i className="fas fa-briefcase tab-icon"></i>
              <span className="tab-text-desktop">Experience</span>
              <TabValidationIndicator {...tabValidation.experience} />
            </Tab>
            <Tab className={tabStyles}>
              <i className="fas fa-calendar tab-icon"></i>
              <span className="tab-text-desktop">Availability</span>
              <TabValidationIndicator {...tabValidation.availability} />
            </Tab>
          </TabList>

          <TabPanel>
            <ProfileCompleteness profile={state.profile} className="integrated-below-tabs mb-3" />
            <ValidationErrorSummary
              validationErrors={state.validation_errors}
              nestedValidationErrors={state.nested_validation_errors}
              experiences={state.profile.experiences}
              educations={state.profile.educations}
              dispatch={dispatch}
            />
            <GeneralInfo />
            <div className="button-group justify-center">
              <button
                className="modern-btn btn-primary"
                onClick={nextTab}
                disabled={state.processing}
              >
                Continue to Documents
                <i className="fas fa-arrow-right ms-2"></i>
              </button>
            </div>
          </TabPanel>

          <TabPanel>
            <ProfileCompleteness profile={state.profile} className="integrated-below-tabs mb-3" />
            <ValidationErrorSummary
              validationErrors={state.validation_errors}
              nestedValidationErrors={state.nested_validation_errors}
              experiences={state.profile.experiences}
              educations={state.profile.educations}
              dispatch={dispatch}
            />
            <Documents />
            <div className="button-group justify-center">
              <button
                className="modern-btn btn-primary"
                onClick={nextTab}
                disabled={state.processing}
              >
                Continue to Experience
                <i className="fas fa-arrow-right ms-2"></i>
              </button>
            </div>
          </TabPanel>

          <TabPanel>
            <ProfileCompleteness profile={state.profile} className="integrated-below-tabs mb-3" />
            <ValidationErrorSummary
              validationErrors={state.validation_errors}
              nestedValidationErrors={state.nested_validation_errors}
              experiences={state.profile.experiences}
              educations={state.profile.educations}
              dispatch={dispatch}
            />
            <Experience />
            <div className="button-group justify-center">
              <button
                className="modern-btn btn-primary"
                onClick={nextTab}
                disabled={state.processing}
              >
                Continue to Availability
                <i className="fas fa-arrow-right ms-2"></i>
              </button>
            </div>
          </TabPanel>

          <TabPanel>
            <ProfileCompleteness profile={state.profile} className="integrated-below-tabs mb-3" />
            <ValidationErrorSummary
              validationErrors={state.validation_errors}
              nestedValidationErrors={state.nested_validation_errors}
              experiences={state.profile.experiences}
              educations={state.profile.educations}
              dispatch={dispatch}
            />
            <Availability />
            <div className="button-group justify-center">
              <button
                className="modern-btn btn-success"
                onClick={submitForm}
                disabled={state.processing}
              >
                <i className="fas fa-save me-2"></i>
                Save Profile
              </button>
            </div>
          </TabPanel>
        </Tabs>
      )}
    </ProfileFormContext.Provider>
  );
}
